import React, { useCallback, useMemo } from 'react';
import { ArrowPathIcon, ChartBarIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useClientExportFlow } from '../../hooks/useClientExportFlow';
import { SectionLabel } from './KpiDashboardContent';
import KpiDashboardExportMenu from './KpiDashboardExportMenu';
import { fetchMsrReportRawData } from './kpiDashboardApi';
import { exportMsrRawDataExcel } from './msrReportRawExport';
import KpiChangeLabeledBarChart from './charts/KpiChangeLabeledBarChart';
import MsrDistributionDonut from './charts/MsrDistributionDonut';
import MsrCiTreemapChart from './charts/MsrCiTreemapChart';
import MsrObservationBox from './MsrObservationBox';
import MsrReportSlide from './MsrReportSlide';
import MsrChartCapture from './MsrChartCapture';
import { exportMsrSectionPdf } from './msrReportPdfExport';
import { exportMsrSectionExcel, type MsrSectionKey } from './msrReportSectionExport';
import type { MsrReportData } from './msrReportTypes';
import MsrAzureCostSection, { type AzureCostSectionQueryState } from './MsrAzureCostSection';
import { azureCostMonthYear } from './useAzureCost';
import type {
  AzureLast3MonthsAnalysisResult,
  AzureSubscriptionCostResult,
} from '../../types/azureCost';

export interface KpiMsrReportTabProps {
  data: MsrReportData | undefined;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  onRetry?: () => void;
  customerSelected: boolean;
  dateFrom: string;
  dateTo: string;
  filterValues: Record<string, string | string[]>;
  azureCostCustomerId?: string;
  azureCostCustomerName?: string;
  azureCostSubscription: AzureCostSectionQueryState<AzureSubscriptionCostResult>;
  azureCostLast3MonthsAnalysis: AzureCostSectionQueryState<AzureLast3MonthsAnalysisResult>;
}

function MsrReportHeaderStrip({
  customerName,
  dateFrom,
  dateTo,
}: {
  customerName: string;
  dateFrom: string;
  dateTo: string;
}) {
  return (
    <div className="overflow-hidden rounded-xl border border-slate-200/90 bg-white shadow-sm shadow-slate-900/5 ring-1 ring-slate-950/[0.04]">
      <div className="h-1 bg-gradient-to-r from-[#2F5597] via-[#4472C4] to-sky-500" aria-hidden />
      <div className="grid grid-cols-1 divide-y divide-slate-100 sm:grid-cols-2 sm:divide-x sm:divide-y-0">
        <div className="px-5 py-4 sm:px-6 sm:py-5">
          <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Customer</span>
          <p className="mt-1 text-2xl font-bold tabular-nums tracking-tight text-slate-900 sm:text-3xl">
            {customerName}
          </p>
        </div>
        <div className="px-5 py-4 sm:px-6 sm:py-5">
          <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Report period</span>
          <p className="mt-1 text-lg font-semibold tabular-nums tracking-tight text-slate-900">
            {dateFrom} – {dateTo}
          </p>
        </div>
      </div>
    </div>
  );
}

function MsrReportSkeleton() {
  return (
    <div className="space-y-8" aria-busy="true" aria-label="Loading MSR report">
      <div className="h-24 animate-pulse rounded-xl bg-slate-200/80" />
      <div>
        <div className="mb-3 h-3 w-48 animate-pulse rounded bg-slate-200" />
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div className="h-72 animate-pulse rounded-xl border border-slate-200/80 bg-slate-100/80 shadow-sm" />
          <div className="h-72 animate-pulse rounded-xl border border-slate-200/80 bg-slate-100/80 shadow-sm" />
        </div>
      </div>
      <div>
        <div className="mb-3 h-3 w-56 animate-pulse rounded bg-slate-200" />
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div className="h-72 animate-pulse rounded-xl border border-slate-200/80 bg-slate-100/80 shadow-sm" />
          <div className="h-72 animate-pulse rounded-xl border border-slate-200/80 bg-slate-100/80 shadow-sm" />
        </div>
      </div>
    </div>
  );
}

const KpiMsrReportTab: React.FC<KpiMsrReportTabProps> = ({
  data,
  isLoading,
  isFetching,
  error,
  onRetry,
  customerSelected,
  dateFrom,
  dateTo,
  filterValues,
  azureCostCustomerId = '',
  azureCostCustomerName = 'Customer',
  azureCostSubscription,
  azureCostLast3MonthsAnalysis,
}) => {
  const { month: azureMonth, year: azureYear } = azureCostMonthYear(dateTo);
  const { run, modal } = useClientExportFlow();

  const ciChartRows = useMemo(() => {
    if (!data) return [];
    return data.ciSlaDistribution.ciWise;
  }, [data]);

  const makeSectionExportHandlers = useCallback(
    (section: MsrSectionKey) => ({
      onExportPdf: () => {
        if (!data) return;
        void (async () => {
          try {
            const file = await exportMsrSectionPdf(section, data, dateFrom, dateTo);
            await run({
              downloadButtonLabel: 'Download PDF',
              work: async () => ({
                files: file,
                summaryLines: ['Your PDF is ready to download.'],
              }),
            });
          } catch (e) {
            await run({
              downloadButtonLabel: 'Download PDF',
              work: async () => {
                throw e;
              },
            });
          }
        })();
      },
      onExportExcel: () => {
        if (!data) return;
        void (async () => {
          try {
            const file = await exportMsrSectionExcel(section, data, dateFrom, dateTo);
            await run({
              downloadButtonLabel: 'Download Excel',
              work: async () => ({
                files: file,
                summaryLines: ['Your Excel file is ready to download.'],
              }),
            });
          } catch (e) {
            await run({
              downloadButtonLabel: 'Download Excel',
              work: async () => {
                throw e;
              },
            });
          }
        })();
      },
    }),
    [data, dateFrom, dateTo, run]
  );

  const teamExport = useMemo(() => makeSectionExportHandlers('team-ticket'), [makeSectionExportHandlers]);
  const resolutionExport = useMemo(
    () => makeSectionExportHandlers('resolution-priority'),
    [makeSectionExportHandlers]
  );
  const ciSlaExport = useMemo(() => makeSectionExportHandlers('ci-sla'), [makeSectionExportHandlers]);

  const handleExportRawData = useCallback(() => {
    void run({
      downloadButtonLabel: 'Download Excel',
      work: async () => {
        const timezoneOffset = new Date().getTimezoneOffset();
        const raw = await fetchMsrReportRawData(filterValues, timezoneOffset);
        if (!raw.rows.length) {
          throw new Error('No tickets match the current customer and filters.');
        }
        const file = exportMsrRawDataExcel(raw.customerName, dateFrom, dateTo, raw.rows);
        return {
          files: file,
          summaryLines: [
            `Exported ${raw.rows.length.toLocaleString()} ticket row(s) to Excel.`,
          ],
        };
      },
    });
  }, [run, filterValues, dateFrom, dateTo]);

  const azureCostCustomerLabel =
    azureCostSubscription.data?.customerId === azureCostCustomerId
      ? azureCostSubscription.data.customerName
      : azureCostCustomerName;

  const azureCostSection = (
    <MsrAzureCostSection
      customerId={azureCostCustomerId}
      customerName={azureCostCustomerLabel}
      month={azureMonth}
      year={azureYear}
      dateFrom={dateFrom}
      dateTo={dateTo}
      subscription={azureCostSubscription}
      last3MonthsAnalysis={azureCostLast3MonthsAnalysis}
    />
  );

  if (!customerSelected) {
    return (
      <div className="flex min-h-[420px] flex-col items-center justify-center rounded-2xl border border-slate-200/90 bg-gradient-to-br from-white via-slate-50/50 to-indigo-50/20 p-12 text-center shadow-lg shadow-slate-900/5 ring-1 ring-slate-950/[0.04]">
        <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-100 to-violet-100 shadow-inner">
          <ChartBarIcon className="h-10 w-10 text-indigo-500" aria-hidden />
        </div>
        <h2 className="mt-6 text-lg font-semibold tracking-tight text-slate-800">Select a customer</h2>
        <p className="mt-2 max-w-md text-sm leading-relaxed text-slate-500">
          The MSR report is generated per customer. Use the <strong>Customer</strong> filter above to
          choose a customer — ticket charts, Azure cost analysis, and observations will update automatically.
        </p>
      </div>
    );
  }

  if (isLoading && !data) {
    return (
      <>
        {modal}
        <MsrReportSkeleton />
        {azureCostSection}
      </>
    );
  }

  if (error && !data) {
    return (
      <>
        {modal}
        <div className="rounded-xl border border-red-200/80 bg-white p-8 text-center shadow-sm">
          <ExclamationTriangleIcon className="mx-auto h-10 w-10 text-red-500" aria-hidden />
          <p className="mt-3 text-sm font-semibold text-red-900">Could not load MSR report</p>
          <p className="mt-1 text-sm text-red-700/90">{error.message}</p>
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="mt-4 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
            >
              Try again
            </button>
          )}
        </div>
        {azureCostSection}
      </>
    );
  }

  if (!data) {
    return (
      <>
        {modal}
        {azureCostSection}
      </>
    );
  }

  const { teamTicketDistribution, resolutionPriorityDistribution, ciSlaDistribution } = data;
  const exportDisabled = isFetching;

  return (
    <>
      {modal}
      <div className="relative space-y-8">
        {isFetching && (
          <div
            className="pointer-events-none absolute right-0 top-0 z-10 flex items-center gap-1.5 rounded-full border border-indigo-200/80 bg-white/95 px-3 py-1.5 text-xs font-medium text-indigo-800 shadow-md shadow-indigo-900/10 backdrop-blur-sm"
            aria-live="polite"
          >
            <ArrowPathIcon className="h-3.5 w-3.5 animate-spin text-indigo-600" aria-hidden />
            Updating
          </div>
        )}

        {error && data && (
          <div className="mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-amber-200/90 bg-amber-50/90 px-4 py-3 text-sm text-amber-950 shadow-sm">
            <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-amber-600" aria-hidden />
            <span className="min-w-0 flex-1">Showing cached data. Latest refresh failed: {error.message}</span>
            {onRetry && (
              <button
                type="button"
                onClick={onRetry}
                className="shrink-0 font-semibold text-amber-900 underline decoration-amber-600/50 underline-offset-2 hover:text-amber-950"
              >
                Retry
              </button>
            )}
          </div>
        )}

        <section className="mb-8">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0 flex-1">
              <SectionLabel id="msr-overview-heading">MSR overview</SectionLabel>
            </div>
            <div className="mb-3 shrink-0">
              <KpiDashboardExportMenu
                disabled={exportDisabled}
                onExportExcel={handleExportRawData}
                showPdf={false}
                ariaLabel="Export MSR raw ticket data"
                triggerLabel="Export Raw Data"
              />
            </div>
          </div>
          <MsrReportHeaderStrip
            customerName={data.customerName}
            dateFrom={dateFrom}
            dateTo={dateTo}
          />
        </section>

        <MsrReportSlide
          id="msr-slide-team-ticket"
          title="Team & Ticket Type Wise Distribution"
          observation={<MsrObservationBox observations={teamTicketDistribution.observations} />}
          onExportPdf={teamExport.onExportPdf}
          onExportExcel={teamExport.onExportExcel}
          exportDisabled={exportDisabled}
        >
          <div className="grid grid-cols-1 items-stretch gap-4 lg:grid-cols-2">
            <MsrChartCapture
              id="msr-chart-team-ticket-team"
              chartTitle="Team Wise Ticket Data"
              downloadDisabled={exportDisabled || teamTicketDistribution.teamWise.length === 0}
            >
              <KpiChangeLabeledBarChart
                title="Team Wise Ticket Data"
                labels={teamTicketDistribution.teamWise.map((r) => r.label)}
                values={teamTicketDistribution.teamWise.map((r) => r.count)}
                datasetLabel="Tickets"
                valueFormat="count"
                emptyMessage="No team data for this customer in the selected period."
              />
            </MsrChartCapture>
            <MsrChartCapture
              id="msr-chart-team-ticket-category"
              chartTitle="Category Wise Ticket Data"
              downloadDisabled={exportDisabled || teamTicketDistribution.ticketTypeWise.length === 0}
            >
              <KpiChangeLabeledBarChart
                title="Category Wise Ticket Data"
                labels={teamTicketDistribution.ticketTypeWise.map((r) => r.label)}
                values={teamTicketDistribution.ticketTypeWise.map((r) => r.count)}
                datasetLabel="Tickets"
                valueFormat="count"
                emptyMessage="No ticket types recorded for this customer."
              />
            </MsrChartCapture>
          </div>
        </MsrReportSlide>

        <MsrReportSlide
          id="msr-slide-resolution-priority"
          title="Ticket Resolution & Priority Wise Distribution"
          observation={
            <MsrObservationBox observations={resolutionPriorityDistribution.observations} />
          }
          onExportPdf={resolutionExport.onExportPdf}
          onExportExcel={resolutionExport.onExportExcel}
          exportDisabled={exportDisabled}
        >
          <div className="grid grid-cols-1 items-stretch gap-4 lg:grid-cols-2">
            <MsrChartCapture
              id="msr-chart-resolution-bins"
              chartTitle="Resolution Time Binned Wise Ticket Data"
              downloadDisabled={
                exportDisabled || resolutionPriorityDistribution.resolutionBins.length === 0
              }
            >
              <MsrDistributionDonut
                title="Resolution Time Binned Wise Ticket Data"
                rows={resolutionPriorityDistribution.resolutionBins}
              />
            </MsrChartCapture>
            <MsrChartCapture
              id="msr-chart-resolution-priority"
              chartTitle="Priority Wise Ticket Data"
              downloadDisabled={
                exportDisabled || resolutionPriorityDistribution.priorityWise.length === 0
              }
            >
              <MsrDistributionDonut
                title="Priority Wise Ticket Data"
                rows={resolutionPriorityDistribution.priorityWise}
              />
            </MsrChartCapture>
          </div>
        </MsrReportSlide>

        <MsrReportSlide
          id="msr-slide-ci-sla"
          title="Top CI Wise Tickets Distribution & Monthly SLA Achievement"
          observation={<MsrObservationBox observations={ciSlaDistribution.observations} />}
          onExportPdf={ciSlaExport.onExportPdf}
          onExportExcel={ciSlaExport.onExportExcel}
          exportDisabled={exportDisabled}
        >
          <div className="grid grid-cols-1 items-stretch gap-4 lg:grid-cols-2">
            <MsrChartCapture
              id="msr-chart-ci-treemap"
              chartTitle="CI Wise Ticket Data"
              hideImageDownload
              downloadDisabled={exportDisabled || ciChartRows.length === 0}
            >
              <MsrCiTreemapChart
                title="CI Wise Ticket Data"
                rows={ciChartRows}
                imageDownloadElementId="msr-chart-ci-treemap"
                imageDownloadDisabled={exportDisabled || ciChartRows.length === 0}
                emptyMessage="No configuration items linked to tickets for this customer."
              />
            </MsrChartCapture>
            <MsrChartCapture
              id="msr-chart-ci-sla"
              chartTitle="Ticket SLA Status"
              downloadDisabled={
                exportDisabled ||
                (ciSlaDistribution.slaStatus.achieved === 0 &&
                  ciSlaDistribution.slaStatus.breached === 0)
              }
            >
              <KpiChangeLabeledBarChart
                title="Ticket SLA Status"
                labels={['Achieved', 'Breached']}
                values={[ciSlaDistribution.slaStatus.achieved, ciSlaDistribution.slaStatus.breached]}
                datasetLabel="Tickets"
                valueFormat="count"
                emptyMessage="No SLA data for this customer."
              />
            </MsrChartCapture>
          </div>
        </MsrReportSlide>

        {azureCostSection}
      </div>
    </>
  );
};

export default KpiMsrReportTab;
